import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapPin, ArrowLeft, ArrowRight, ExternalLink, FileText, CheckCircle2, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import Footer from '../components/sections/Footer';
import SEO from '../components/SEO';
import { getOrganizationSchema, getBreadcrumbSchema } from '../seo/schemas';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// TinyMCE saves uploads with relative URLs like `api/uploads/x.png` (no leading slash).
// On a detail page like `/pre-approved/Pasadena`, the browser resolves them to
// `/pre-approved/api/uploads/x.png` which 404s. Normalise to absolute paths.
const fixRelativeUploads = (html) =>
  (html || '').replace(/(src|href)=(["'])(api\/uploads\/)/g, '$1=$2/$3');

// ========== LIST PAGE ==========
const PreApproved = () => {
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const [items, setItems] = useState([]);
  const [banner, setBanner] = useState({ title: 'Pre-Approved ADU Plans', subtitle: 'City-approved ADU plans for faster permitting in California', images: [] });
  const [slideIdx, setSlideIdx] = useState(0);

  useEffect(() => {
    const load = async () => {
      try {
        const [paRes, bannerRes] = await Promise.all([
          axios.get(`${API}/admin/pre-approved`),
          axios.get(`${API}/admin/settings/preapproved_banner`),
        ]);
        setItems(paRes.data?.items || []);
        if (bannerRes.data?.value?.title) setBanner(bannerRes.data.value);
      } catch {}
    };
    load();
  }, []);

  const bannerImages = banner.images?.length > 0 ? banner.images : (banner.image ? [banner.image] : []);
  useEffect(() => {
    if (bannerImages.length <= 1) return;
    const interval = setInterval(() => setSlideIdx(prev => (prev + 1) % bannerImages.length), 5000);
    return () => clearInterval(interval);
  }, [bannerImages.length]);

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="pre-approved-page">
      <SEO
        title={isZh ? '加州预审批 ADU 方案 | 各市预批准户型加快许可 | ALIGCUS 阿里建筑' : 'California Pre-Approved ADU Plans | City-Approved ADU Designs for Faster Permitting | ALIGCUS'}
        description={isZh ? '查询加州各市预审批 ADU 方案：使用预批方案可大幅缩短许可周期、节省审查费用。覆盖洛杉矶、帕萨迪纳、圣何塞等城市。' : 'California city-approved Pre-Approved ADU plans — drastically shorten permit timelines and save plan check fees. Covering Los Angeles, Pasadena, San Jose and more.'}
        canonical={isZh ? 'https://www.aligcus.com/zh/pre-approved' : 'https://www.aligcus.com/pre-approved'}
        lang={isZh ? 'zh' : 'en'}
        alternates={[
          { lang: 'en', url: 'https://www.aligcus.com/pre-approved' },
          { lang: 'zh-CN', url: 'https://www.aligcus.com/zh/pre-approved' },
        ]}
        schemas={[
          getOrganizationSchema(isZh ? 'zh' : 'en'),
          getBreadcrumbSchema([
            { name: isZh ? '首页' : 'Home', url: isZh ? 'https://www.aligcus.com/zh/' : 'https://www.aligcus.com/' },
            { name: isZh ? '预审批方案' : 'Pre-Approved', url: isZh ? 'https://www.aligcus.com/zh/pre-approved' : 'https://www.aligcus.com/pre-approved' },
          ]),
        ]}
      />

      {/* Banner */}
      <section className="relative flex items-center justify-center overflow-hidden" style={{ minHeight: '300px', maxHeight: '60vh' }}>
        <div className="absolute inset-0 bg-[#7c1d1d]">
          {bannerImages.map((img, i) => (
            <img key={i} src={img} alt="" className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${i === slideIdx % bannerImages.length ? 'opacity-80' : 'opacity-0'}`} />
          ))}
        </div>
        <div className="relative text-center px-4 sm:px-6 py-12 sm:py-0">
          <h1 className="text-3xl sm:text-5xl md:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight">
            {isZh ? (banner.title_zh || '预审批 ADU 方案') : (banner.title_en || banner.title)}
          </h1>
          <p className="text-sm sm:text-xl md:text-2xl text-white/80 font-light">
            {isZh ? (banner.subtitle_zh || '城市预批准的 ADU 户型，加快您的建造许可') : (banner.subtitle_en || banner.subtitle)}
          </p>
        </div>
        {bannerImages.length > 1 && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-10">
            {bannerImages.map((_, i) => (
              <button key={i} onClick={() => setSlideIdx(i)} className={`h-2 rounded-full transition-all ${i === slideIdx % bannerImages.length ? 'w-8 bg-white' : 'w-2 bg-white/50'}`} />
            ))}
          </div>
        )}
      </section>

      <main className="flex-grow py-12">
        <div className="max-w-5xl mx-auto px-6">
          <RegionView items={items} isZh={isZh} />
        </div>
      </main>
      <Footer />
    </div>
  );
};

// ========== REGION VIEW ==========
const RegionView = ({ items, isZh }) => {
  const [selectedRegion, setSelectedRegion] = useState(null);
  const navigate = useNavigate();

  if (!items || items.length === 0) {
    return <p className="text-center text-gray-400 py-8" data-testid="pre-approved-empty">{isZh ? '暂无数据' : 'No pre-approved plans available yet.'}</p>;
  }

  const northItems = items.filter(r => r.region === 'North');
  const southItems = items.filter(r => r.region === 'South');
  const sdItems = items.filter(r => r.region === 'San Diego');

  const groupByCounty = (regs) => {
    const map = {};
    regs.forEach(r => {
      const county = r.county || 'Other';
      if (!map[county]) map[county] = [];
      map[county].push(r);
    });
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]));
  };

  const currentItems =
    selectedRegion === 'North' ? northItems :
    selectedRegion === 'South' ? southItems :
    selectedRegion === 'San Diego' ? sdItems : null;
  const countyGroups = currentItems ? groupByCounty(currentItems) : null;

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-10">
        {[
          { key: 'South', label: 'Southern California', labelZh: '南加州', count: southItems.length },
          { key: 'North', label: 'Northern California', labelZh: '北加州', count: northItems.length },
          { key: 'San Diego', label: 'San Diego', labelZh: '圣地亚哥', count: sdItems.length, direct: true },
        ].map(region => (
          <button
            key={region.key}
            onClick={() => {
              if (region.direct) {
                const sd = items.find(r => r.region === 'San Diego');
                if (sd) navigate(`/pre-approved/${encodeURIComponent(sd.city)}`);
                return;
              }
              setSelectedRegion(selectedRegion === region.key ? null : region.key);
            }}
            data-testid={`region-${region.key.toLowerCase().replace(' ','-')}`}
            className={`relative overflow-hidden rounded-2xl p-6 sm:p-8 text-left transition-all duration-300 hover:scale-[1.02] ${selectedRegion === region.key ? 'ring-4 ring-red-400 shadow-2xl' : 'shadow-lg hover:shadow-xl'}`}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-red-700 to-red-900" />
            <div className="relative text-white">
              <h3 className="text-lg sm:text-2xl font-bold flex items-center justify-between">
                <span>{isZh ? region.labelZh : region.label}</span>
                {region.count > 0 && <span className="bg-white/20 text-sm px-2 py-0.5 rounded-full">{region.count}</span>}
              </h3>
            </div>
          </button>
        ))}
      </div>

      {countyGroups && countyGroups.length > 0 && (
        <div className="space-y-8" data-testid="county-list">
          {countyGroups.map(([county, cities]) => (
            <div key={county}>
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                <div className="w-2 h-8 bg-red-600 rounded-full" />
                {county}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {cities.map(item => (
                  <Link
                    key={item.id}
                    to={`/pre-approved/${encodeURIComponent(item.city)}`}
                    data-testid={`pre-approved-city-${item.city}`}
                    className="flex items-center gap-3 px-4 py-3 bg-gray-100 rounded-lg hover:bg-red-50 transition-colors group"
                  >
                    <div className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-gray-800 font-semibold text-lg group-hover:text-red-900">{item.city}</span>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ========== DETAIL PAGE ==========
export const PreApprovedDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const [item, setItem] = useState(null);
  const [regulation, setRegulation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/admin/pre-approved/${encodeURIComponent(id)}`);
        setItem(r.data);
        // If linked to regulation, fetch it
        if (r.data?.regulation_id) {
          try {
            const regs = await axios.get(`${API}/admin/regulations`);
            const rg = (regs.data?.regulations || []).find(x => x.id === r.data.regulation_id);
            if (rg) setRegulation(rg);
          } catch {}
        }
      } catch {
        setItem(null);
      } finally {
        setLoading(false);
      }
    };
    load();
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-gray-500">{isZh ? '加载中...' : 'Loading...'}</div>;

  if (!item) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <div className="flex-1 flex items-center justify-center px-6 pt-32 pb-16 text-center">
          <div>
            <h1 className="text-3xl font-bold mb-3">{isZh ? '未找到方案' : 'Plan Not Found'}</h1>
            <Button onClick={() => navigate(isZh ? '/zh/pre-approved' : '/pre-approved')} className="bg-red-600 hover:bg-red-700">
              <ArrowLeft className="w-4 h-4 mr-1" />{isZh ? '返回列表' : 'Back to List'}
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const seoTitle = isZh
    ? `${item.city} 预审批 ADU 方案 | 加州 ALIGCUS 阿里建筑`
    : `${item.city} Pre-Approved ADU Plan | California ALIGCUS Construction`;
  const seoDesc = isZh
    ? `${item.city} 城市预审批 ADU 方案详情。使用预批方案可加速许可流程。免费咨询 (626) 803-8000。`
    : `${item.city} city pre-approved ADU plan details. Use pre-approved plans to accelerate permitting. Free consultation (626) 803-8000.`;
  const canonical = `https://www.aligcus.com${isZh ? '/zh' : ''}/pre-approved/${encodeURIComponent(item.city)}`;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SEO
        title={seoTitle}
        description={seoDesc}
        canonical={canonical}
        lang={isZh ? 'zh' : 'en'}
        alternates={[
          { lang: 'en', url: `https://www.aligcus.com/pre-approved/${encodeURIComponent(item.city)}` },
          { lang: 'zh-CN', url: `https://www.aligcus.com/zh/pre-approved/${encodeURIComponent(item.city)}` },
        ]}
        schemas={[
          getOrganizationSchema(isZh ? 'zh' : 'en'),
          getBreadcrumbSchema([
            { name: isZh ? '首页' : 'Home', url: isZh ? 'https://www.aligcus.com/zh/' : 'https://www.aligcus.com/' },
            { name: isZh ? '预审批方案' : 'Pre-Approved', url: isZh ? 'https://www.aligcus.com/zh/pre-approved' : 'https://www.aligcus.com/pre-approved' },
            { name: item.city, url: canonical },
          ]),
        ]}
      />

      <section className="bg-red-900 text-white py-16 pt-28">
        <div className="max-w-5xl mx-auto px-6">
          <Link to={isZh ? '/zh/pre-approved' : '/pre-approved'} className="inline-flex items-center text-red-200 hover:text-white mb-6 text-sm">
            <ArrowLeft className="w-4 h-4 mr-1" />{isZh ? '所有城市' : 'All Cities'}
          </Link>
          <div className="flex items-center gap-3 mb-3">
            <CheckCircle2 className="w-8 h-8 text-red-300" />
            <span className="text-red-300 text-sm uppercase tracking-wider font-semibold">{isZh ? '预审批方案' : 'Pre-Approved Plan'}</span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-bold mb-2">{item.city}</h1>
          <div className="flex flex-wrap gap-2 text-red-100 text-sm mt-3">
            {item.county && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{item.county}</span>}
            {item.region && <span className="bg-white/10 px-2 py-0.5 rounded">{item.region}</span>}
          </div>
        </div>
      </section>

      <main className="flex-grow py-12">
        <div className="max-w-5xl mx-auto px-6 grid lg:grid-cols-[1fr_280px] gap-10">
          <article className="prose prose-lg max-w-none prose-headings:scroll-mt-28 prose-img:rounded-lg" data-testid="pre-approved-content">
            {item.image && <img src={item.image} alt={item.city} className="w-full rounded-xl mb-6" />}
            {(() => {
              // 中文版优先用 _zh 字段，留空时回退英文（用户配置 a 兜底策略）
              const html = isZh ? (item.content_html_zh || item.content_html) : item.content_html;
              return html ? (
                <div dangerouslySetInnerHTML={{ __html: fixRelativeUploads(html) }} />
              ) : (
                <p className="text-gray-500">{isZh ? '即将推出，敬请期待' : 'Content coming soon. Stay tuned!'}</p>
              );
            })()}
          </article>

          {/* Sidebar */}
          <aside className="space-y-4">
            {regulation && (
              <div className="bg-gray-50 rounded-xl p-5 border border-gray-200" data-testid="linked-regulation">
                <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2">{isZh ? '相关法规' : 'Related Regulation'}</div>
                <Link to={`/adu-regulations/${encodeURIComponent(regulation.city)}`} className="flex items-center justify-between gap-2 text-red-700 hover:text-red-900 font-semibold">
                  <span className="flex items-center gap-2"><FileText className="w-4 h-4" />{regulation.city} ADU {isZh ? '法规' : 'Regulations'}</span>
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            )}
            <div className="bg-red-50 rounded-xl p-5 border border-red-200">
              <div className="font-semibold text-red-900 mb-2">{isZh ? '免费咨询' : 'Free Consultation'}</div>
              <p className="text-sm text-red-800 mb-3">{isZh ? '了解此预审批方案是否适合您的物业' : 'Find out if this pre-approved plan suits your property'}</p>
              <Link to={isZh ? '/zh/quote' : '/quote'}>
                <Button className="w-full bg-red-600 hover:bg-red-700">{isZh ? '获取报价' : 'Get a Quote'}<ArrowRight className="w-4 h-4 ml-1" /></Button>
              </Link>
            </div>
          </aside>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PreApproved;
